import { useState, useRef, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import './ChatWidget.css';

const CHAT_API_URL = '/api/chat';
const WHATSAPP_NUMBER = '56942703188';
const WELCOME_MESSAGE = '¡Hola! Soy tu asistente XCoin. Puedo ayudarte con tu cuenta, transacciones, staking, o cualquier duda técnica. ¿En qué te puedo ayudar?';

const ChatBubbleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const BotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" y1="16" x2="8" y2="16" /><line x1="16" y1="16" x2="16" y2="16" />
  </svg>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const CollapseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

function generateSessionId() {
  return 'dash_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
}

export default function ChatWidget() {
  const { userData } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: WELCOME_MESSAGE }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load conversation history when chat opens for the first time
  const loadHistory = useCallback(async () => {
    if (historyLoaded || !userData?.uid) return;
    try {
      const res = await fetch(`/api/chat/history?user_id=${userData.uid}`);
      const data = await res.json();
      if (data.success && data.messages && data.messages.length > 0) {
        setMessages(data.messages);
        setSessionId(data.session_id || generateSessionId());
      } else {
        setSessionId(generateSessionId());
      }
    } catch (err) {
      console.error('Error loading chat history:', err);
      setSessionId(generateSessionId());
    }
    setHistoryLoaded(true);
  }, [userData, historyLoaded]);

  useEffect(() => {
    if (isOpen && !historyLoaded) {
      loadHistory();
    }
  }, [isOpen, historyLoaded, loadHistory]);

  const getWhatsAppUrl = () => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    const context = lastUserMsg ? lastUserMsg.content : '';
    const text = encodeURIComponent(
      `Hola, vengo desde el dashboard de XCoin y necesito ayuda.${context ? `\n\nMi consulta: ${context}` : ''}`
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      const apiMessages = updatedMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      // Ensure we have a session ID
      let sid = sessionId;
      if (!sid) {
        sid = generateSessionId();
        setSessionId(sid);
      }

      const res = await fetch(CHAT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          mode: 'dashboard',
          session_id: sid,
          user_id: userData?.uid || '',
          user_name: userData?.fullName || '',
          user_email: userData?.email || ''
        })
      });

      const data = await res.json();

      if (data.success && data.message) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.error || 'Lo siento, hubo un error. ¿Podrías intentar de nuevo?',
          isError: true
        }]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'No pude conectarme al servidor. Por favor intenta más tarde o contacta a un ejecutivo por WhatsApp.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className={`xcoin-chat-window ${isExpanded ? 'expanded' : ''}`}>
          {/* Header */}
          <div className="xcoin-chat-header">
            <div className="xcoin-chat-header-info">
              <div className="xcoin-chat-avatar">
                <BotIcon />
              </div>
              <div className="xcoin-chat-header-text">
                <h4>Asistente XCoin</h4>
                <span>En línea</span>
              </div>
            </div>
            <div className="xcoin-chat-header-actions">
              <button className="xcoin-chat-expand" onClick={() => setIsExpanded(!isExpanded)} aria-label={isExpanded ? 'Reducir ventana' : 'Expandir ventana'}>
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </button>
              <button className="xcoin-chat-close" onClick={() => setIsOpen(false)} aria-label="Cerrar chat">
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="xcoin-chat-messages">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`xcoin-chat-msg ${msg.isError ? 'error' : msg.role}`}
              >
                {msg.content}
              </div>
            ))}
            {isLoading && (
              <div className="xcoin-chat-typing">
                <span /><span /><span />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* WhatsApp Escalation */}
          <div className="xcoin-chat-whatsapp">
            <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon />
              Hablar con un ejecutivo por WhatsApp
            </a>
          </div>

          {/* Input */}
          <div className="xcoin-chat-input-area">
            <input
              ref={inputRef}
              type="text"
              placeholder="Escribe tu mensaje..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              maxLength={500}
            />
            <button
              className="xcoin-chat-send"
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              aria-label="Enviar mensaje"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      <button
        className={`xcoin-chat-bubble ${isOpen ? 'chat-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Cerrar asistente' : 'Abrir asistente virtual'}
      >
        {isOpen ? <CloseIcon /> : <ChatBubbleIcon />}
        {!isOpen && <div className="xcoin-chat-badge" />}
      </button>
    </>
  );
}
